import type { RemittanceGroupingMode } from '@capture/shared';
import { useFormConfigStore } from '../state/store';
import './BasicsSection.css';

const GROUPING_MODE_OPTIONS: { value: RemittanceGroupingMode; label: string }[] = [
  { value: 'coupon_cycle', label: 'Coupon cycle' },
  { value: 'payment_opens', label: 'Payment opens' },
];

/**
 * SP6 Task 3 — Basics section of the Remittance job-config tab. Every write
 * routes through `updateRemittance` (the single mutator every remittance
 * section shares); reads come straight from `contentV3.remittance` so the
 * seeded default (`groupingMode: 'coupon_cycle'`) shows even before the
 * config object exists on the draft.
 */
export function BasicsSection() {
  const remittance = useFormConfigStore((s) => s.contentV3?.remittance);
  const updateRemittance = useFormConfigStore((s) => s.updateRemittance);

  const groupingMode = remittance?.groupingMode ?? 'coupon_cycle';
  const dualControl = remittance?.dualControl ?? false;

  return (
    <section className="remittance-basics">
      <h3 className="remittance-basics__heading">Basics</h3>

      <label className="remittance-basics__field" htmlFor="remittance-basics-grouping-mode">
        Grouping mode
        <select
          id="remittance-basics-grouping-mode"
          value={groupingMode}
          onChange={(e) => updateRemittance({ groupingMode: e.target.value as RemittanceGroupingMode })}
        >
          {GROUPING_MODE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>

      <label className="remittance-basics__field" htmlFor="remittance-basics-tolerance">
        Balance tolerance (cents)
        <input
          id="remittance-basics-tolerance"
          type="number"
          value={remittance?.balanceToleranceCents ?? ''}
          onChange={(e) => {
            const raw = e.target.value;
            if (raw === '') {
              updateRemittance({ balanceToleranceCents: undefined });
              return;
            }
            const n = Number(raw);
            if (Number.isNaN(n)) return;
            updateRemittance({ balanceToleranceCents: n });
          }}
        />
      </label>

      <label className="remittance-basics__field remittance-basics__field--checkbox" htmlFor="remittance-basics-dual-control">
        <input
          id="remittance-basics-dual-control"
          type="checkbox"
          checked={dualControl}
          onChange={(e) => updateRemittance({ dualControl: e.target.checked })}
        />
        Dual control
      </label>
      <p className="remittance-basics__hint">
        Dual control requires the split pipeline shape (Remittance Key + Remittance Balance steps)
      </p>
    </section>
  );
}
